import { useCallback } from 'react';

import { formatTimeStampToObject } from '../utils/date';
import useSearchParams from './useSearchParams';

export type ClearUrlParams = (config?: { ignoreParams?: string[] }) => void;

interface UseFilterProps {
  startDateName?: string;
  endDateName?: string;
  searchName?: string;
  sortName?: string;
  orderBy?: string;
}

export const propsDefault: UseFilterProps = {
  startDateName: 'startDate',
  endDateName: 'endDate',
  searchName: 's',
  sortName: 'sortName',
  orderBy: 'orderBy',
};

export const getQuery = (query: URLSearchParams, name: string) => {
  if (query?.has(name)) return query.get(name);
  return '';
};

export function useFilter(options?: UseFilterProps) {
  options = { ...propsDefault, ...options };

  const { searchParams: query, navigateWithSearchParam } = useSearchParams();
  const search = query.get(options?.searchName || '');
  const startDate = Number(query.get(options?.startDateName || ''));
  const endDate = Number(query.get(options?.endDateName || ''));
  const startDateObject = startDate && formatTimeStampToObject(startDate);
  const endDateObject = endDate && formatTimeStampToObject(endDate);
  const sortName = query.get(options?.sortName || '');
  const orderBy = query.get(options?.orderBy || '');

  const aggregateQuery = (key: string, value: any) => {
    if (query.has(key)) {
      if (value) {
        query.set(key, Array.isArray(value) ? value.join(',') : value);
      } else {
        query.delete(key);
      }
    } else {
      query.append(key, value);
    }
  };

  const pushQuery = (key: string, value: any) => {
    aggregateQuery(key, value);
    navigateWithSearchParam({ search: query.toString() });
  };

  const pushQueries = (q: Record<string, any>) => {
    Object.entries(q).forEach(([key, value]) => {
      aggregateQuery(key, value);
    });
    navigateWithSearchParam({ search: query.toString() });
  };

  const pushGroupQuery = (groups: Record<string, string[]>) => {
    Object.entries(groups).forEach(([key, values]) => {
      pushQuery(key, values);
    });
  };

  const popQuery = (key: string) => {
    if (query.has(key)) {
      query.delete(key);
    }
    navigateWithSearchParam({ search: query.toString() });
  };

  const popQueries = (keys: string[]) => {
    if (!keys?.length) return;
    keys.forEach((k) => {
      if (query.has(k)) {
        query.delete(k);
      }
    });
    navigateWithSearchParam({ search: query.toString() });
  };

  const clearQuery = (exceptionList: string[]) => {
    const arr = [...query.entries()];
    for (const [key] of arr) {
      if (!exceptionList.includes(key)) {
        query.delete(key);
      }
    }
    const search = query.toString();
    navigateWithSearchParam({ search });
  };

  return {
    query,
    navigateWithSearchParam,
    search,
    startDate,
    endDate,
    startDateObject,
    endDateObject,
    pushQuery,
    pushQueries,
    popQuery,
    popQueries,
    pushGroupQuery,
    clearQuery,
    sortName,
    orderBy,
  } as const;
}
